import React, { useState, useEffect } from "react";
import { Col, Container, Row } from "reactstrap";
import s from "./styles.scss";
import withStyles from "isomorphic-style-loader/withStyles";
import OrganizationForm from "./organizationDetailsForm";
import { StepItem } from "../../../../../toolkit/stepper";
import {
  DescriptionOutlined,
  LocationOnOutlined,
  SupportAgentOutlined,
} from "@mui/icons-material";
import SettingsIcon from "@mui/icons-material/Settings";
import BusinessAddressForm from "./businessAddressForm";
import LegalDocumentsForm from "./legalDocumentsForm";
import SupportContactForm from "./supportContactForm";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Wizard } from "react-use-wizard";
import StepFooter from "./components/WizardFooter";
import { organizationFormSchema } from "./organizationDetailsForm";
import { businessAddressFormSchema } from "./businessAddressForm";
import { legalDocumentsFormSchema } from "./legalDocumentsForm";
import { supportContactFormSchema } from "./supportContactForm";
import { serviceCaller, ServiceCallerProps } from "../../../../composition";
import { saveCharityInfo, fetchCharityInfo } from "../../../../../utils/fetch";
import LoadingSpinner from "../../../../../toolkit/loadingSpinner";
import { connect } from "react-redux";
import { firebaseConnect, isLoaded } from "react-redux-firebase";
import get from "lodash.get";
import { useLoading } from "../../../../../hooks/useLoading";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import {
  useOrganizationProgress,
  getOrganizationProgressFromState,
} from "./hooks/useOrganizationProgress";
import { useWizardState } from "./hooks/useWizardState";
import { OrganizationFormData, MINIMUM_LOADING_DURATION_MS } from "./types";
import { transformFormDataToCharityInfo, transformCharityInfoToFormData } from "./utils/transformers";
import WizardBreadcrumb from "../common/WizardBreadcrumb";
import WizardStepper from "../components/WizardStepper";
import StepTransition from "../components/StepTransition";
import ErrorDisplay from "../../../../../toolkit/errorDisplay";
import ErrorAlert from "../../../../../toolkit/errorAlert";
import SnackbarNotification from "../../../../../toolkit/snackbarNotification";
import { WizardStep } from "../common/types";

export const stepDefs: WizardStep[] = [
  {
    label: "Organization Details",
    icon: <SettingsIcon fontSize="small" />,
    Component: OrganizationForm,
    schema: organizationFormSchema,
  },
  {
    label: "Website Setup",
    icon: <SupportAgentOutlined fontSize="small" />,
    Component: SupportContactForm,
    schema: supportContactFormSchema,
  },
  {
    label: "Business Address",
    icon: <LocationOnOutlined fontSize="small" />,
    Component: BusinessAddressForm,
    schema: businessAddressFormSchema,
  },
  {
    label: "Terms & conditions",
    icon: <DescriptionOutlined fontSize="small" />,
    Component: LegalDocumentsForm,
    schema: legalDocumentsFormSchema,
  },
];

const organizationWizardSchema = stepDefs
  .map(d => d.schema)
  .reduce((acc, s) => acc.concat(s), stepDefs[0].schema);

const steps: StepItem[] = stepDefs.map(d => ({ label: d.label, icon: d.icon }));

const fieldsByStep = stepDefs.map(d => Object.keys(d.schema.fields));

interface OrganizationSetupWizardProps extends ServiceCallerProps {
  charityId?: string;
  firebaseDraft?: any;
  draftLoaded: boolean;
  firebase?: any;
  embedded?: boolean;
  onExit?: () => void;
  maintainMode?: boolean;
}

const OrganizationSetupWizard: React.FC<OrganizationSetupWizardProps> = ({
  config,
  serviceAccessToken,
  charityId,
  firebaseDraft,
  draftLoaded,
  firebase,
  embedded = true,
  onExit,
  maintainMode = false,
}) => {
  const { loading: isSubmitting, runWithLoading: runFormSubmission } = useLoading();
  const { loading: isFetching, runWithLoading: runDataFetch } = useLoading();
  const { snackbarProps, showSnackbar } = useSnackbar();
  const totalSteps = stepDefs.length;
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [existingCharityInfo, setExistingCharityInfo] = useState<any>(null);

  const methods = useForm<OrganizationFormData>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(organizationWizardSchema),
    defaultValues: firebaseDraft?.formData || {},
  });

  const {
    error: progressError,
    saveDraft,
    markAsSubmitted,
    clearDraft,
  } = useOrganizationProgress({ charityId, firebase });

  const { startIndex, isReady } = useWizardState(
    firebaseDraft,
    draftLoaded,
    totalSteps,
    methods
  );

  useEffect(() => {
    if (!draftLoaded || !charityId || !firebase) {
      return;
    }

    const prefillFromCharitySetup = async () => {
      const database = firebase.database();
      const [charitySetupSnapshot, headerUrlSnapshot] = await Promise.all([
        database.ref(`${charityId}/charitySetup`).once("value"),
        database.ref(`${charityId}/config/headerRedirectUrl`).once("value"),
      ]);
      const charitySetup = charitySetupSnapshot.val();
      const headerRedirectUrl = headerUrlSnapshot.val() as string | null;

      if (charitySetup?.name && !methods.getValues("organizationName")) {
        methods.setValue("organizationName", charitySetup.name);
      }
      if (charitySetup?.name && !methods.getValues("legalOrganizationName")) {
        methods.setValue("legalOrganizationName", charitySetup.name);
      }
      if (headerRedirectUrl && !methods.getValues("organizationWebsiteUrl")) {
        methods.setValue("organizationWebsiteUrl", headerRedirectUrl);
      }
    };

    prefillFromCharitySetup();
  }, [draftLoaded, charityId, firebase, methods]);

  useEffect(() => {
    const fetchBackendData = async () => {
      if (!draftLoaded || !charityId || !config || !serviceAccessToken) {
        return;
      }

      const shouldLoadInfo =
        maintainMode || firebaseDraft?.status === "completed";

      if (shouldLoadInfo) {
        setFetchError(null);

        try {
          await runDataFetch(async () => {
            const charityInfo = await fetchCharityInfo(
              config.adminServiceUrl || "",
              charityId,
              serviceAccessToken
            );

            if (charityInfo) {
              setExistingCharityInfo(charityInfo);
              const formData = transformCharityInfoToFormData(charityInfo);
              methods.reset(formData);
            }
          }, 1000);
        } catch (error) {
          console.error('Failed to fetch organization data:', error);
          if (maintainMode || firebaseDraft?.status === "completed") {
            setFetchError('Failed to load organization data');
          }
        }
      }
    };

    fetchBackendData();
  }, [
    draftLoaded,
    firebaseDraft?.status,
    charityId,
    config,
    serviceAccessToken,
    maintainMode,
  ]);


  const handleSaveDraft = async (data: any, currentStep: number) => {
    if (maintainMode || firebaseDraft?.status === 'completed') {
      return;
    }

    try {
      await saveDraft(data, currentStep, totalSteps);
    } catch (error) {
      console.error('Failed to save draft:', error);
    }
  };

  const onFinish = async (data: OrganizationFormData) => {
    setSaveError(null);

    const success = await runFormSubmission(async () => {
      const isEditMode =
        maintainMode || firebaseDraft?.status === "completed";

      try {
        if (!isEditMode) {
          await markAsSubmitted();
        }

        const formInfo = transformFormDataToCharityInfo(data);
        const existing = existingCharityInfo || {};
        const charityInfo = {
          ...existing,
          ...formInfo,
          addressDetails: {
            ...(existing.addressDetails || {}),
            ...formInfo.addressDetails,
          },
        };

        await saveCharityInfo(
          config?.adminServiceUrl || "",
          charityId || "",
          serviceAccessToken || "",
          charityInfo
        );

        if (!isEditMode || maintainMode) {
          await clearDraft();
        }

        return true;
      } catch (error) {
        if (!isEditMode && firebaseDraft?.status === 'submitted') {
          try {
            await saveDraft(data, totalSteps - 1, totalSteps);
          } catch (revertError) {
            console.error('Failed to revert Firebase state:', revertError);
          }
        }

        const errorMessage = error instanceof Error
          ? error.message
          : "Failed to save organization information. Please try again.";
        setSaveError(errorMessage);

        return false;
      }
    }, 1000);

    if (success) {
      const isEditMode =
        maintainMode || firebaseDraft?.status === "completed";
      const organizationName = methods.getValues("organizationName") || "Your organization";
      const message = isEditMode
        ? `${organizationName} has been successfully updated!`
        : `${organizationName} has been successfully configured!`;

      showSnackbar(message, "success");

      setTimeout(() => {
        if (onExit) {
          onExit();
        }
      }, 3000);
    }
  };

  const renderMainContent = () => {
    const isContentReady = draftLoaded && isReady && !isFetching && !progressError && !fetchError;
    const isContentLoading = !isContentReady;

    if (progressError || fetchError) {
      return (
        <ErrorDisplay
          errorMessage={progressError || fetchError || "An unexpected error occurred"}
          title="Organization Setup Error"
          showBack={!!onExit}
          onBack={onExit}
          className={s.fadeIn}
        />
      );
    }

    return (
      <>
        <FormProvider {...methods}>
          <form noValidate className={s.fadeIn}>
            <Wizard
              key={`wizard-${startIndex}`}
              startIndex={startIndex}
              header={
                <WizardStepper
                  steps={steps}
                  onBack={onExit}
                  showBack={!embedded}
                  canSelectStep={index =>
                    maintainMode ||
                    firebaseDraft?.status === "completed" ||
                    index <= (firebaseDraft?.currentSubStep ?? startIndex)
                  }
                />
              }
              footer={
                embedded ? (
                  <StepFooter
                    onFinish={onFinish}
                    fieldsByStep={fieldsByStep}
                    loading={isSubmitting}
                    loadingText="Processing"
                    loadingAnimation="pulse"
                    onSaveProgress={handleSaveDraft}
                  />
                ) : (
                  <div className={s.wizardStepFooterPanel}>
                    <StepFooter
                      onFinish={onFinish}
                      fieldsByStep={fieldsByStep}
                      loading={isSubmitting}
                      loadingText="Processing"
                      loadingAnimation="pulse"
                      onSaveProgress={handleSaveDraft}
                    />
                  </div>
                )
              }
              wrapper={
                <StepTransition
                  contentClassName={embedded ? undefined : s.wizardStepPanel}
                />
              }
            >
              {stepDefs.map(({ Component }, index) => (
                <Component key={index} />
              ))}
            </Wizard>
          </form>
        </FormProvider>

        {saveError && (
          <Row className="mt-3 justify-content-center">
            <Col md="8">
              <ErrorAlert
                message={saveError}
              />
            </Col>
          </Row>
        )}
      </>
    );
  };


  const showLoadingSpinner = !(draftLoaded && isReady && !isFetching && !progressError && !fetchError);

  return (
    <>
      <Container fluid className={embedded ? "vh-100" : s.fullScreenContainer}>
        <Row className="h-100">
          <Col md="12" className={s.wizardContainer}>
            {!embedded && showLoadingSpinner && (
              <WizardBreadcrumb onBack={onExit} showBreadcrumb />
            )}
            {showLoadingSpinner ? (
              <div className="mt-5">
                <LoadingSpinner
                  title="Loading Organization Setup"
                  message="Please wait while we load your data and initialize the wizard."
                  className={s.fadeIn}
                />
              </div>
            ) : (
              <div className={embedded ? undefined : s.wizardShell}>
                {renderMainContent()}
              </div>
            )}
          </Col>
        </Row>
      </Container>

      <SnackbarNotification
        {...snackbarProps}
        position={{ vertical: "top", horizontal: "center" }}
      />
    </>
  );
};

const mapStateToProps = (state: any) => {
  return getOrganizationProgressFromState(state);
};

export default connect(mapStateToProps)(
  firebaseConnect((props: any, store: any) => {
    const charityId = store.getState().user.currentCharity?.id;
    return charityId
      ? [`${charityId}/charitySetup/organization`]
      : [];
  })(serviceCaller(withStyles(s)(OrganizationSetupWizard)))
);
